/**
 * Which service a connector reaches, for its icon — the ONE place that decides it.
 *
 * The marks themselves are generated (serviceIcons.data.ts, from Simple Icons, else the
 * owner's official mark; see SERVICE_ICONS_ATTRIBUTION.md). A connector is recognised by
 * the host it calls first (its MCP address or API base URL — a host covers its
 * subdomains), then by its name or slug as whole words ("Jira Cloud", "notion_workspace"). Anything else — a server
 * of someone's own, a service with no mark — keeps the letter avatar.
 */
import { SERVICE_ICONS } from './serviceIcons.data';

interface ServiceIconBase {
  key: string;
  title: string;
  hosts: readonly string[];
  names: readonly string[];
}

/** A Simple Icons mark: one path in one colour. */
export interface SimpleServiceIcon extends ServiceIconBase {
  /** The mark's colour on the white tile (the brand colour, or a neutral one when that is too light). */
  color: string;
  /** SVG path data on a 24×24 view box. */
  path: string;
  viewBox?: undefined;
  paths?: undefined;
}

/** One shape of an owner's own mark, as the owner drew it. */
export interface ServiceIconPath {
  d: string;
  /** '#RRGGBB', or 'none' for a stroke-only shape. */
  fill: string;
  fillRule?: 'evenodd';
  stroke?: string;
  strokeWidth?: number;
  strokeMiterlimit?: number;
  transform?: string;
}

/** An owner's official mark (for a service Simple Icons does not carry): its own view box, several colours. */
export interface OfficialServiceIcon extends ServiceIconBase {
  viewBox: string;
  paths: readonly ServiceIconPath[];
}

export type ServiceIconData = SimpleServiceIcon | OfficialServiceIcon;

export interface ServiceIdentity {
  name?: string | null;
  slug?: string | null;
  /** An MCP server's address or an API's base URL. */
  url?: string | null;
}

function hostOf(url: string | null | undefined): string {
  if (!url) return '';
  try {
    return new URL(url).hostname.toLowerCase().replace(/\.$/, '');
  } catch {
    return '';
  }
}

/** "Jira Cloud", "jira_cloud", "Google-Analytics 4" → " jira cloud ", " google analytics 4 " */
function words(text: string): string {
  return ` ${text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()} `;
}

/** Hosts shared by several of one company's products: the connector's name says which one. */
const UMBRELLA = new Set(['atlassian']);

export function serviceIconFor({ name, slug, url }: ServiceIdentity): ServiceIconData | null {
  const host = hostOf(url);
  const byHost = host
    ? SERVICE_ICONS.find(icon => icon.hosts.some(h => host === h || host.endsWith(`.${h}`))) ?? null
    : null;
  const said = `${words(name || '')}${words(slug || '')}`;
  const byName = SERVICE_ICONS.find(icon => icon.names.some(n => said.includes(` ${n} `))) ?? null;
  if (byHost && !(UMBRELLA.has(byHost.key) && byName)) return byHost;
  return byName;
}
